import React, { useState, useRef, useEffect } from 'react';
import { 
  Sun, Cloud, CloudRain, CloudSnow, CloudLightning, 
  Droplets, Wind, Eye, Settings, ArrowLeft, Move3D, Loader2, Save, MapPin
} from 'lucide-react';
import SearchComponent from '@/components/ui/animated-glowing-search-bar';
import { useWeather } from '@/contexts/WeatherContext';
import { AnimatedWeatherIcon } from '@/components/ui/animated-weather-icon';

export const WeatherWidget = () => {
  const { weatherData, config, loading, updateConfig, fetchWeather } = useWeather();
  const [dimensions, setDimensions] = useState({ width: 380, height: 280 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isFlipped, setIsFlipped] = useState(false);
  const [tempLocation, setTempLocation] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Function to clean condition text for display (remove night prefixes)
  const getDisplayCondition = (condition: string) => {
    // Remove night prefixes added by the API for animation logic
    return condition
      .replace(/^Night Rain - /, '')
      .replace(/^Night Sky - /, '')
      .replace(/^Night Moon - /, '')
      .replace(/^Night /, '');
  };

  // Popular city suggestions for predictive search
  const popularCities = [
    'New York, NY', 'Los Angeles, CA', 'London, UK', 'Paris, France',
    'Tokyo, Japan', 'Sydney, Australia', 'Berlin, Germany', 'Toronto, Canada',
    'Chicago, IL', 'Miami, FL', 'San Francisco, CA', 'Rome, Italy',
    'Barcelona, Spain', 'Amsterdam, Netherlands', 'Vienna, Austria',
    'Dubai, UAE', 'Singapore', 'Hong Kong', 'Seoul, South Korea',
    'Mumbai, India', 'Beijing, China', 'São Paulo, Brazil'
  ];

  const getWeatherIcon = (condition: string) => {
    const lowerCondition = condition.toLowerCase();
    if (lowerCondition.includes('sunny') || lowerCondition.includes('clear')) {
      return Sun;
    } else if (lowerCondition.includes('rain') || lowerCondition.includes('drizzle')) {
      return CloudRain;
    } else if (lowerCondition.includes('snow') || lowerCondition.includes('blizzard')) {
      return CloudSnow;
    } else if (lowerCondition.includes('thunder')) {
      return CloudLightning;
    } else {
      return Cloud;
    }
  };

  const convertTemperature = (temp: number, unit: 'celsius' | 'fahrenheit') => {
    if (unit === 'fahrenheit') {
      return Math.round((temp * 9/5) + 32);
    }
    return temp;
  };

  // Unit conversion functions
  const convertWindSpeed = (kmh: number, system: 'metric' | 'imperial') => {
    if (system === 'imperial') {
      return Math.round(kmh * 0.621371); // km/h to mph
    }
    return kmh;
  };

  const convertVisibility = (km: number, system: 'metric' | 'imperial') => {
    if (system === 'imperial') {
      return Math.round(km * 0.621371); // km to miles
    }
    return km;
  };

  // Theme helper functions
  const getThemeClasses = () => {
    return config.theme === 'light' 
      ? {
          container: 'bg-gradient-to-br from-white/95 via-sky-50/90 to-blue-100/85 backdrop-blur-xl',
          overlay: 'bg-gradient-to-br from-blue-400/20 via-cyan-300/15 to-purple-400/20',
          button: 'bg-white/80 hover:bg-white/95 border-gray-300/60 shadow-lg shadow-gray-400/20',
          text: 'text-gray-900',
          mutedText: 'text-gray-700',
          lightText: 'text-gray-600'
        }
      : {
          container: 'bg-gradient-to-br from-background/90 via-background/95 to-background/98 backdrop-blur-xl',
          overlay: 'bg-gradient-to-br from-primary/10 via-accent/8 to-secondary/12',
          button: 'bg-background/40 hover:bg-background/60 border-border/40',
          text: 'text-foreground/95',
          mutedText: 'text-foreground/80',
          lightText: 'text-foreground/60'
        };
  };

  const themeClasses = getThemeClasses();

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: dimensions.width,
      height: dimensions.height
    });
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!isResizing) return;
    
    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;
    
    const newWidth = Math.max(300, resizeStart.width + deltaX);
    const newHeight = Math.max(200, resizeStart.height + deltaY);
    
    setDimensions({ width: newWidth, height: newHeight });
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, resizeStart]);

  const handleConfigSave = () => {
    const newLocation = tempLocation.trim() || 'auto';
    const newConfig = { ...config, location: newLocation };
    updateConfig(newConfig);
    setIsFlipped(false);
    if (newLocation !== config.location) {
      fetchWeather(newLocation);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    if (!isFlipped) {
      setTempLocation(config.location === 'auto' ? '' : config.location);
    }
  };

  if (loading) {
    return (
      <div 
        style={{ width: `${dimensions.width}px`, height: `${dimensions.height}px` }}
        className="flex items-center justify-center bg-gradient-to-br from-background/5 via-background/10 to-background/20 backdrop-blur-xl rounded-3xl border border-border/20 shadow-2xl"
      >
        <Loader2 className="w-8 h-8 animate-spin text-foreground/60" />
      </div>
    );
  }

  if (!weatherData) {
    return (
      <div 
        style={{ width: `${dimensions.width}px`, height: `${dimensions.height}px` }}
        className="flex items-center justify-center bg-gradient-to-br from-background/5 via-background/10 to-background/20 backdrop-blur-xl rounded-3xl border border-border/20 shadow-2xl"
      >
        <p className="text-sm text-foreground/60 text-center font-medium">Weather data unavailable</p>
      </div>
    );
  }

  // Calculate temperature with unit conversion
  const displayTemp = convertTemperature(weatherData.temperature, config.temperatureUnit);
  const tempUnit = config.temperatureUnit === 'celsius' ? '°C' : '°F';

  return (
    <div 
      style={{ 
        width: `${dimensions.width}px`, 
        height: `${dimensions.height}px`,
        perspective: '1000px'
      }}
      className="relative"
    >
      <div 
        ref={containerRef}
        className="w-full h-full relative transition-transform duration-700"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* Front Side - Weather Display */}
        <div 
          className={`absolute inset-0 ${themeClasses.container} rounded-3xl shadow-2xl border border-border/20 overflow-hidden`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          {/* Settings Button */}
          <button
            onClick={handleFlip}
            className={`absolute top-4 right-4 z-50 w-8 h-8 rounded-full ${themeClasses.button} flex items-center justify-center transition-all duration-300 backdrop-blur-sm border group`}
          >
            <Settings className={`w-4 h-4 ${themeClasses.mutedText} group-hover:rotate-45 transition-transform duration-300`} />
          </button>

          {/* Luxurious gradient overlay */}
          <div className={`absolute inset-0 ${themeClasses.overlay}`}></div>
          
          {/* Resize Handle */}
          <div
            onMouseDown={handleResizeStart}
            className="absolute bottom-3 right-3 w-4 h-4 cursor-se-resize opacity-30 hover:opacity-60 transition-all duration-300 z-50 flex items-center justify-center group"
          >
            <Move3D className={`w-3.5 h-3.5 ${themeClasses.lightText} rotate-45 drop-shadow-sm group-hover:scale-110 transition-transform`} />
          </div>

          {/* Content Container */}
          <div className="relative z-10 h-full flex flex-col justify-between p-8">
            
            {/* Header Section */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MapPin className={`w-4 h-4 ${themeClasses.lightText}`} />
                <div className={`text-lg font-semibold ${themeClasses.text} tracking-wide`}>
                  {weatherData.location}
                </div>
              </div>
              <div className={`text-sm ${themeClasses.lightText} font-medium`}>
                {weatherData.country} • {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </div>
            </div>

            {/* Temperature Section */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className={`text-6xl font-extralight ${themeClasses.text} tracking-tighter`}>
                    {displayTemp}
                  </span>
                  <span className={`text-2xl font-light ${themeClasses.mutedText}`}>{tempUnit}</span>
                </div>
                <div className={`text-sm ${themeClasses.lightText} font-medium capitalize`}>
                  {getDisplayCondition(weatherData.condition)}
                </div>
              </div>
              
              {/* Weather Icon */}
              <div className="flex items-center justify-center">
                <AnimatedWeatherIcon 
                  condition={weatherData.condition}
                  className="w-16 h-16"
                  size={64}
                />
              </div>
            </div>

            {/* Conditional Stats Grid */}
            {(config.showHumidity || config.showWindSpeed || config.showVisibility) && (
              <div className="grid grid-cols-3 gap-4">
                {config.showHumidity && (
                  <div className="space-y-1 text-center">
                    <Droplets className={`w-4 h-4 ${themeClasses.lightText} mx-auto`} />
                    <div className={`text-xs ${themeClasses.lightText} font-medium`}>Humidity</div>
                    <div className={`text-sm font-semibold ${themeClasses.mutedText}`}>{weatherData.humidity}%</div>
                  </div>
                )}
                {config.showWindSpeed && (
                  <div className={`space-y-1 text-center ${config.showHumidity && config.showVisibility ? 'border-x border-border/20' : ''}`}>
                    <Wind className={`w-4 h-4 ${themeClasses.lightText} mx-auto`} />
                    <div className={`text-xs ${themeClasses.lightText} font-medium`}>Wind</div>
                    <div className={`text-sm font-semibold ${themeClasses.mutedText}`}>
                      {convertWindSpeed(weatherData.windSpeed, config.unitSystem)}{' '}
                      {config.unitSystem === 'metric' ? 'km/h' : 'mph'}
                    </div>
                  </div>
                )}
                {config.showVisibility && (
                  <div className="space-y-1 text-center">
                    <Eye className={`w-4 h-4 ${themeClasses.lightText} mx-auto`} />
                    <div className={`text-xs ${themeClasses.lightText} font-medium`}>Visibility</div>
                    <div className={`text-sm font-semibold ${themeClasses.mutedText}`}>
                      {convertVisibility(weatherData.visibility, config.unitSystem)}{' '}
                      {config.unitSystem === 'metric' ? 'km' : 'mi'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Back Side - Configuration Panel */}
        <div 
          className={`absolute inset-0 ${themeClasses.container} rounded-3xl shadow-2xl border border-border/20 overflow-hidden`}
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          {/* Close Settings Button */}
          <button
            onClick={handleFlip}
            className={`absolute top-4 right-4 z-50 w-8 h-8 rounded-full ${themeClasses.button} flex items-center justify-center transition-all duration-300 backdrop-blur-sm border group`}
          >
            <ArrowLeft className={`w-4 h-4 ${themeClasses.mutedText} group-hover:-translate-x-0.5 transition-transform duration-300`} />
          </button>

          {/* Luxurious gradient overlay */}
          <div className={`absolute inset-0 ${themeClasses.overlay}`}></div>

          <div className="relative z-10 h-full p-6 flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto pr-2 space-y-5">
              <h3 className={`${themeClasses.text} font-semibold text-lg tracking-wide`}>Settings</h3>
              
              {/* Location Input */}
              <div className="space-y-2">
                <label className={`block ${themeClasses.mutedText} text-sm font-medium`}>Location</label>
                <div className="px-1">
                  <SearchComponent
                    value={tempLocation}
                    onChange={setTempLocation}
                    onSelect={handleConfigSave}
                    placeholder="Enter city name or leave empty for auto"
                    suggestions={popularCities}
                    className=""
                  />
                </div>
                <p className={`text-xs ${themeClasses.lightText} ml-4`}>
                  Select from suggestions or press Enter • Empty = auto-detect
                </p>
              </div>

              {/* Temperature Unit */}
              <div className="space-y-2">
                <label className={`block ${themeClasses.mutedText} text-sm font-medium`}>Temperature</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateConfig({ ...config, temperatureUnit: 'celsius' })}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      config.temperatureUnit === 'celsius' 
                        ? 'bg-primary/20 text-primary border border-primary/30' 
                        : config.theme === 'light'
                            ? 'bg-white/80 text-gray-900 border border-gray-300 hover:bg-white/95'
                            : 'bg-background/10 text-foreground/80 border border-border/20 hover:bg-background/20'
                    }`}
                  >
                    °C
                  </button>
                  <button
                    onClick={() => updateConfig({ ...config, temperatureUnit: 'fahrenheit' })}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      config.temperatureUnit === 'fahrenheit' 
                        ? 'bg-primary/20 text-primary border border-primary/30' 
                        : config.theme === 'light'
                            ? 'bg-white/80 text-gray-900 border border-gray-300 hover:bg-white/95'
                            : 'bg-background/10 text-foreground/80 border border-border/20 hover:bg-background/20'
                    }`}
                  >
                    °F
                  </button>
                </div>
              </div>

              {/* Theme Toggle */}
              <div className="space-y-2">
                <label className={`block ${themeClasses.mutedText} text-sm font-medium`}>Theme</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateConfig({ ...config, theme: 'dark' })}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      config.theme === 'dark' 
                        ? 'bg-primary/20 text-primary border border-primary/30' 
                        : config.theme === 'light'
                            ? 'bg-white/80 text-gray-900 border border-gray-300 hover:bg-white/95'
                            : 'bg-background/10 text-foreground/80 border border-border/20 hover:bg-background/20'
                    }`}
                  >
                    Dark
                  </button>
                  <button
                    onClick={() => updateConfig({ ...config, theme: 'light' })}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      config.theme === 'light' 
                        ? 'bg-primary/20 text-primary border border-primary/30' 
                        : 'bg-white/80 text-gray-900 border border-gray-300 hover:bg-white/95'
                    }`}
                  >
                    Light
                  </button>
                </div>
              </div>

              {/* Unit System Toggle */}
              <div className="space-y-2">
                <label className={`block ${themeClasses.mutedText} text-sm font-medium`}>Units</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateConfig({ ...config, unitSystem: 'metric' })}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      config.unitSystem === 'metric' 
                        ? 'bg-primary/20 text-primary border border-primary/30' 
                        : config.theme === 'light'
                            ? 'bg-white/80 text-gray-900 border border-gray-300 hover:bg-white/95'
                            : 'bg-background/10 text-foreground/80 border border-border/20 hover:bg-background/20'
                    }`}
                  >
                    Metric
                  </button>
                  <button
                    onClick={() => updateConfig({ ...config, unitSystem: 'imperial' })}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      config.unitSystem === 'imperial' 
                        ? 'bg-primary/20 text-primary border border-primary/30' 
                        : config.theme === 'light'
                            ? 'bg-white/80 text-gray-900 border border-gray-300 hover:bg-white/95'
                            : 'bg-background/10 text-foreground/80 border border-border/20 hover:bg-background/20'
                    }`}
                  >
                    Imperial
                  </button>
                </div>
              </div>

              {/* Display Options */}
              <div className="space-y-3">
                <h4 className={`${themeClasses.mutedText} text-sm font-medium`}>Display</h4>
                
                <div className="space-y-2">
                  {[
                    { key: 'showHumidity', label: 'Humidity', icon: Droplets },
                    { key: 'showWindSpeed', label: 'Wind Speed', icon: Wind },
                    { key: 'showVisibility', label: 'Visibility', icon: Eye }
                  ].map(({ key, label, icon: Icon }) => (
                    <label key={key} className="flex items-center gap-3 cursor-pointer group py-1">
                      <input
                        type="checkbox"
                        checked={config[key as keyof typeof config] as boolean}
                        onChange={(e) => updateConfig({ ...config, [key]: e.target.checked })}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                        config[key as keyof typeof config] 
                          ? 'bg-primary/20 border-primary/40' 
                          : 'border-border/30 group-hover:border-border/50'
                      }`}>
                        {config[key as keyof typeof config] && (
                          <div className="w-2 h-2 bg-primary rounded-sm"></div>
                        )}
                      </div>
                      <Icon className={`w-4 h-4 ${themeClasses.lightText}`} />
                      <span className={`text-sm ${themeClasses.mutedText} font-medium`}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Compact Save Button */}
            <div className="pt-3 border-t border-border/10">
              <button
                onClick={handleConfigSave}
                className="w-full py-2.5 px-4 bg-primary/80 hover:bg-primary text-primary-foreground rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};